import React from 'react';
import styled from '@emotion/styled';
import { Skeleton, Typography } from '@mui/material';
import { type WidgetInstance, SegmentationDashboardWidget } from '@trinity/chat';
import type { LibraryWidgetConfig } from '@trinity/chat/types/dashboard-config';
import { widgetRefOf } from '@trinity/chat/utils/library-widgets';
import { useLibraryWidget, useWidgetLibraries } from '../../../contexts/widget-library/Context.js';
import type { WidgetMapContextValue } from '../../../containers/WidgetGrid/context.js';
import type { WidgetRenderProps } from './WidgetRegistry.js';

/** Registry render for `library_widget` (and the legacy pointer types): resolves and draws the widget. */
export function LibraryWidgetRender({ config }: WidgetRenderProps): React.ReactElement {
  return <LibraryWidgetRenderer pointer={config.config as LibraryWidgetConfig} />;
}

/**
 * Resolves a saved-widget pointer against the host's WidgetLibrary for its source and renders it.
 * View mode prunes missing pointers before render (see pruneMissingLibraryWidgets), so the
 * placeholders below show only in the builder, where editors can remove them.
 */
export function LibraryWidgetRenderer({
  pointer,
}: {
  pointer: Pick<LibraryWidgetConfig, 'widgetId' | 'source'>;
}): React.ReactElement {
  const ref = widgetRefOf(pointer);
  const state = useLibraryWidget(ref);
  const { widgetComponentMap } = useWidgetLibraries();

  if (state.status === 'ready') {
    return <LibraryWidgetContent widget={state.widget} widgetComponentMap={widgetComponentMap} />;
  }
  if (state.status === 'missing') return <Placeholder>Widget removed</Placeholder>;
  if (state.status === 'unavailable') return <Placeholder>Library unavailable</Placeholder>;
  if (state.status === 'error') return <Placeholder>Widget couldn&apos;t load</Placeholder>;
  return <Skeleton variant="rounded" height="100%" />;
}

/** Draws a resolved library widget with the host's component for its widget type. */
export function LibraryWidgetContent({
  widget,
  widgetComponentMap,
}: {
  widget: WidgetInstance;
  widgetComponentMap: WidgetMapContextValue;
}): React.ReactElement {
  const Component = widgetComponentMap[widget.type] as React.ComponentType<object> | undefined;
  if (!Component) return <Placeholder>Unknown widget type: {widget.type}</Placeholder>;

  // Same as the legacy WidgetGrid item: the library row's title sits above the widget, except
  // media, whose card renders its own title. `data-citation-widget-id` is the anchor chat
  // citations deep-link to (`?widgetId=`).
  const isMedia = widget.type === SegmentationDashboardWidget.Media;
  return (
    // Fills its cell: a column that grows, so a widget that grows (a quote card) can use the space.
    <div
      data-citation-widget-id={String(widget.id)}
      style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%' }}
    >
      {!isMedia && widget.title && <WidgetTitle>{widget.title}</WidgetTitle>}
      <Component {...(widget.props ?? {})} {...(isMedia ? { title: widget.title } : {})} />
    </div>
  );
}

const WidgetTitle = styled.h4(({ theme }) => ({
  fontSize: '16px',
  color: theme.palette.text.primary,
  margin: 0,
  paddingBottom: '8px',
}));

export function Placeholder({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic', p: 1, textAlign: 'center' }}>
      {children}
    </Typography>
  );
}
